import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { menuCategories, bowlItems, burritoItems, tacoItems, starterItems, quesadillaItems, allMenuItems, MenuItem } from '../data/menuData';
import { getIconByName } from '../utils/iconHelpers';
import MenuItemCard from './MenuItemCard';
import ItemCustomizationModal from './ItemCustomizationModal';
import { Spa as VegIcon, Yard as LeafIcon, Inventory2 as CartonIcon } from '@mui/icons-material';

interface MenuViewProps {
  onAddToCart: (item: MenuItem) => void;
}

const MenuView: React.FC<MenuViewProps> = ({ onAddToCart }) => {
  const [activeCategory, setActiveCategory] = useState('popular');
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [customizationModalOpen, setCustomizationModalOpen] = useState(false);
  const [vegetarianOnly, setVegetarianOnly] = useState(false);
  const [glutenFreeOnly, setGlutenFreeOnly] = useState(false);
  const [dairyFreeOnly, setDairyFreeOnly] = useState(false);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  // Refs for section elements
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const tabBarRef = useRef<HTMLElement | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const lastActiveUpdateTsRef = useRef(0);
  const lastTabScrollTsRef = useRef(0);

  // Emoji icons for category tabs
  const categoryEmoji = useMemo<Record<string, string>>(() => ({
    popular: '🔥',
    starters: '😋',
    bowls: '🥣',
    burritos: '🌯',
    tacos: '🌮',
    quesadillas: '🧀'
  }), []);

  // Base categories with their items
  const baseCategories = useMemo(() => ([
    {
      id: 'popular',
      name: 'Popular',
      items: allMenuItems
        .slice() // avoid mutating original array
        .sort((a, b) => b.likes - a.likes)
        .slice(0, 4)
    },
    {
      id: 'starters',
      name: 'Starters',
      items: starterItems
    },
    {
      id: 'bowls',
      name: 'Bowls',
      items: bowlItems
    },
    {
      id: 'burritos',
      name: 'Burritos',
      items: burritoItems
    },
    {
      id: 'tacos',
      name: 'Tacos',
      items: tacoItems
    },
    {
      id: 'quesadillas',
      name: 'Quesadillas',
      items: quesadillaItems
    }
  ]), []);

  // Apply dietary filters and hide empty categories
  const categories = useMemo(() => {
    const applyFilters = (items: MenuItem[]) => {
      let filtered = items;
      if (vegetarianOnly) {
        filtered = filtered.filter((it) => it.dietaryTags?.includes('vegetarian'));
      }
      if (glutenFreeOnly) {
        filtered = filtered.filter((it) => it.dietaryTags?.includes('gluten-free'));
      }
      if (dairyFreeOnly) {
        filtered = filtered.filter((it) => it.dietaryTags?.includes('dairy-free'));
      }
      return filtered;
    };

    const c = baseCategories.map((cat) => ({
      ...cat,
      items: cat.id === 'popular'
        ? applyFilters(allMenuItems)
            .slice()
            .sort((a, b) => b.likes - a.likes)
            .slice(0, 4)
        : applyFilters(cat.items)
    })).filter((cat) => cat.items.length > 0);

    return c;
  }, [baseCategories, vegetarianOnly, glutenFreeOnly, dairyFreeOnly]);

  // Ensure active category is valid when filters change
  useEffect(() => {
    if (!categories.find((c) => c.id === activeCategory)) {
      const first = categories[0]?.id;
      if (first) setActiveCategory(first);
    }
  }, [categories, activeCategory]);

  // Set up intersection observer for scroll-based category switching
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (isProgrammaticScrollRef.current) return;
        // Find the entry that's most visible
        let bestEntry: IntersectionObserverEntry | undefined;
        let maxRatio = 0;

        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            bestEntry = entry;
          }
        }

        // Update active category if we have a visible entry
        if (bestEntry && maxRatio > 0.1) {
          const target = bestEntry.target as HTMLElement;
          if (target.id) {
            const validCategory = categories.find(cat => cat.id === target.id);
            if (validCategory && target.id !== activeCategory) {
              const now = performance.now();
              if (now - lastActiveUpdateTsRef.current > 120) {
                lastActiveUpdateTsRef.current = now;
                setActiveCategory(target.id);
                // Auto-scroll tab bar to keep active tab visible
                scrollToActiveTab(target.id);
              }
            }
          }
        }
      },
      {
        threshold: [0.25, 0.5, 0.75],
        rootMargin: isMdUp ? '-18% 0px -32% 0px' : '-110px 0px -32% 0px'
      }
    );

    // Re-observe all sections when component updates
    const observeSections = () => {
      Object.values(sectionRefs.current).forEach((ref) => {
        if (ref && observerRef.current) {
          observerRef.current.observe(ref);
        }
      });
    };

    // Small delay to ensure refs are set and page is loaded
    setTimeout(observeSections, 300);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isMdUp, activeCategory, vegetarianOnly, glutenFreeOnly, dairyFreeOnly]); // Re-run when filters/screen size change

  // Ensure last category highlights when reaching (or near) bottom of the page
  useEffect(() => {
    const handleBottomHighlight = () => {
      if (isProgrammaticScrollRef.current) return;
      const doc = document.documentElement;
      const nearBottom = window.innerHeight + window.scrollY >= (doc.scrollHeight - 2);
      if (nearBottom && categories.length > 0) {
        const lastId = categories[categories.length - 1].id;
        if (activeCategory !== lastId) {
          setActiveCategory(lastId);
          scrollToActiveTab(lastId);
        }
      }
    };

    window.addEventListener('scroll', handleBottomHighlight, { passive: true });
    return () => window.removeEventListener('scroll', handleBottomHighlight);
    // Depend on categories and activeCategory so we respond to filter changes as well
  }, [categories, activeCategory]);

  // Handle initial active tab highlighting when page loads
  useEffect(() => {
    if (!isMdUp) {
      // Small delay to ensure initial scroll position is detected
      setTimeout(() => {
        scrollToActiveTab(activeCategory);
      }, 500);
    }
  }, [isMdUp]);

  // Auto-scroll tab bar to keep active tab visible (throttled)
  const scrollToActiveTab = (categoryId: string) => {
    const now = performance.now();
    if (now - lastTabScrollTsRef.current < 120) return;
    lastTabScrollTsRef.current = now;
    if (!isMdUp && tabBarRef.current) {
      const activeTabIndex = categories.findIndex(cat => cat.id === categoryId);
      if (activeTabIndex !== -1) {
        const tabWidth = 120; // Approximate tab width
        const scrollPosition = activeTabIndex * tabWidth - (tabBarRef.current.clientWidth / 2) + (tabWidth / 2);
        
        tabBarRef.current.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      }
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    // Immediately highlight the clicked tab
    setActiveCategory(categoryId);
    isProgrammaticScrollRef.current = true;
    const element = sectionRefs.current[categoryId];
    if (element) {
      // Calculate offset to account for sticky header and tab bar
      const offset = isMdUp ? 80 : 140; // Desktop vs mobile header heights
      const elementPosition = element.offsetTop - offset;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      
      // Also scroll tab bar to keep clicked tab visible
      scrollToActiveTab(categoryId);
    }
    setTimeout(() => { isProgrammaticScrollRef.current = false; }, 700);
  };

  const handleCardClick = (item: MenuItem) => {
    setSelectedItem(item);
    setCustomizationModalOpen(true);
  };

  const handleCustomizationAddToCart = (item: MenuItem, customizations: any, quantity: number) => {
    // For now, just add to cart normally
    // In a real app, you'd want to store the customizations
    for (let i = 0; i < quantity; i++) {
      onAddToCart(item);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, md: 3 } }}>
      {/* Page Title */}
      <Typography
        variant="h4"
        component="h1"
        sx={{
          mb: { xs: 2, md: 3 },
          fontWeight: 500,
          fontSize: { xs: '1.5rem', md: '1.25rem' },
          letterSpacing: '-0.03125rem',
          lineHeight: '1.625rem',
          px: { xs: 1, md: 0 }
        }}
      >
        Menu
      </Typography>

      {/* Dietary Filters */}
      <Box sx={{ px: { xs: 1, md: 0 }, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          icon={<VegIcon />}
          label="Vegetarian"
          clickable
          onClick={() => setVegetarianOnly((v) => !v)}
          sx={{
            backgroundColor: vegetarianOnly ? '#424242' : 'rgba(66,66,66,0.08)',
            color: vegetarianOnly ? '#fff' : 'inherit',
            '& .MuiChip-icon': { color: vegetarianOnly ? '#fff' : 'inherit' }
          }}
        />
        <Chip
          icon={
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: '2px solid currentColor',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LeafIcon sx={{ fontSize: 14 }} />
            </Box>
          }
          label="Gluten-free"
          clickable
          onClick={() => setGlutenFreeOnly((v) => !v)}
          sx={{
            backgroundColor: glutenFreeOnly ? '#424242' : 'rgba(66,66,66,0.08)',
            color: glutenFreeOnly ? '#fff' : 'inherit',
            '& .MuiChip-icon': { color: glutenFreeOnly ? '#fff' : 'inherit' }
          }}
        />
        <Chip
          icon={
            <Box sx={{ position: 'relative', width: 22, height: 22 }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: '2px solid currentColor',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CartonIcon sx={{ fontSize: 14 }} />
              </Box>
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{ position: 'absolute', inset: 0 }}
              >
                <line x1="4" y1="20" x2="20" y2="4" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
              </Box>
            </Box>
          }
          label="Dairy-free"
          clickable
          onClick={() => setDairyFreeOnly((v) => !v)}
          sx={{
            backgroundColor: dairyFreeOnly ? '#424242' : 'rgba(66,66,66,0.08)',
            color: dairyFreeOnly ? '#fff' : 'inherit',
            '& .MuiChip-icon': { color: dairyFreeOnly ? '#fff' : 'inherit' }
          }}
        />
      </Box>

       {/* Sticky Tab Bar (mobile + desktop) */}
       <Box 
           sx={{ 
             position: 'sticky',
             top: { xs: 56, md: 64 }, // AppBar heights
             zIndex: 100,
             backgroundColor: 'background.paper',
             borderBottom: '1px solid',
             borderColor: 'divider',
             mb: 3,
             mx: { xs: -1, md: 0 }, // extend on mobile to edges
             width: { xs: '100vw', md: '100%' },
             maxWidth: '100%',
             overflow: 'hidden'
           }}
         >
           <Box 
             ref={tabBarRef}
             sx={{ 
               display: 'flex', 
               overflowX: 'auto',
               overflowY: 'hidden',
               px: 1,
               width: '100%',
               minWidth: 0, // Allow flex shrinking
               '&::-webkit-scrollbar': { display: 'none' }, // Hide scrollbar
               scrollbarWidth: 'none', // Firefox
               '-ms-overflow-style': 'none' // IE
             }}
           >
             {categories.map((category) => (
               <Button
                 key={category.id}
                 variant="text"
                 onClick={() => handleCategoryClick(category.id)}
                 sx={{
                   minWidth: 'auto',
                   px: 2,
                   py: 1.5,
                   whiteSpace: 'nowrap',
                   fontSize: '0.9rem',
                   fontWeight: activeCategory === category.id ? 600 : 400,
                   color: activeCategory === category.id ? '#424242' : 'text.secondary',
                   borderBottom: activeCategory === category.id ? '2px solid' : '2px solid transparent',
                   borderColor: activeCategory === category.id ? '#424242' : 'transparent',
                   borderRadius: 0,
                   flexShrink: 0, // Prevent tabs from shrinking
                   '&:hover': {
                     backgroundColor: 'transparent'
                   }
                 }}
               >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span">{category.name}</Box>
                  <Box component="span" aria-hidden="true">{categoryEmoji[category.id] ?? ''}</Box>
                </Box>
               </Button>
             ))}
           </Box>
         </Box>

      {/* All Menu Sections */}
      {categories.map((category) => (
        <Box
          key={category.id}
          id={category.id}
          ref={(el: HTMLElement | null) => {
            sectionRefs.current[category.id] = el;
          }}
          sx={{ mb: 4 }}
        >
          {/* Section Title */}
          <Typography
            variant="h5"
            component="h2"
            sx={{
              mb: 3,
              fontWeight: 600,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              px: { xs: 1, md: 0 }
            }}
          >
            {category.name}
          </Typography>

          {/* Section Items Grid */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(2, 1fr)', 
              lg: 'repeat(3, 1fr)' 
            }, 
            gap: { xs: 2, sm: 2, md: 3 },
            px: { xs: 1, md: 0 }
          }}>
            {category.items.map((item) => (
              <MenuItemCard
                key={item.id}
                item={item}
                onAddToCart={onAddToCart}
                onCardClick={handleCardClick}
              />
            ))}
          </Box>
        </Box>
      ))}

      {/* Customization Modal */}
      <ItemCustomizationModal
        item={selectedItem}
        open={customizationModalOpen}
        onClose={() => setCustomizationModalOpen(false)}
        onAddToCart={handleCustomizationAddToCart}
      />
    </Container>
  );
};

export default MenuView;
